import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { faFileInvoiceDollar, faSpinner } from '@fortawesome/pro-light-svg-icons';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import {
    processReturns,
    fetchProcessingSummary,
    fetchBatchReturnById,
    selectProcessingSummary,
    selectProcessingSummaryLoading,
    selectProcessingCompanyCode,
    selectCurrentBatchReturn,
} from 'store/reducers/batchReturnSlice';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import ActionButton from 'components/ui/ActionButton';
import Spinner from 'components/ui/Spinner';

interface BatchReturnProcessingPanelProps {
    batchReturnId: number;
}

const BatchReturnProcessingPanel = ({ batchReturnId }: BatchReturnProcessingPanelProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const summary = useAppSelector(selectProcessingSummary);
    const loading = useAppSelector(selectProcessingSummaryLoading);
    const processing = useAppSelector(selectProcessingCompanyCode);
    const batchReturn = useAppSelector(selectCurrentBatchReturn);

    const returnIdsByCompany = useMemo(() => {
        if (!batchReturn?.returns) return {};
        const map: Record<string, number[]> = {};
        for (const ret of batchReturn.returns) {
            if (!ret.returnId || !ret.items) continue;
            for (const item of ret.items) {
                const company = item.supplyingCompany;
                if (!company) continue;
                if (!map[company]) map[company] = [];
                if (!map[company].includes(ret.returnId)) map[company].push(ret.returnId);
            }
        }
        return map;
    }, [batchReturn?.returns]);

    const handleProcess = useCallback(
        async (companyCode: string) => {
            const returnIds = returnIdsByCompany[companyCode] ?? [];
            if (returnIds.length === 0) return;
            await dispatch(processReturns({ batchReturnId, returnIds }));
            dispatch(fetchProcessingSummary(batchReturnId));
            dispatch(fetchBatchReturnById(batchReturnId));
        },
        [dispatch, batchReturnId, returnIdsByCompany],
    );

    if (loading && !summary) {
        return (
            <Panel className="p-3" column align="center">
                <Spinner color="text" size="small" />
            </Panel>
        );
    }

    if (!summary || summary.companies.length === 0) {
        return null;
    }

    return (
        <Panel className="p-3" column gap="0.75rem">
            <SectionLabel>{t('batchReturn.processingProgress', 'Processing')}</SectionLabel>
            {summary.companies.map((company) => {
                const canProcess = company.totalRows > 0 && company.processedRows < company.totalRows;
                const isProcessing = processing != null;
                const isCompleted = company.totalRows > 0 && company.processedRows === company.totalRows;

                return (
                    <CompanyRow key={company.companyCode} column gap="0.25rem">
                        <Flex justify="between" align="center">
                            <Text bold fontSize={12}>{company.companyCode}</Text>
                            <Text fontSize={11} color="textLight">
                                {company.processedRows}/{company.totalRows} {t('batchReturn.processed', 'processed')}
                            </Text>
                        </Flex>
                        <ProgressBar>
                            <ProgressFill $percent={(company.processedRows / company.totalRows) * 100} />
                        </ProgressBar>
                        {canProcess && (
                            <ActionButton
                                label={t('batchReturn.createReturnSlip', 'Create Return Slip')}
                                icon={isProcessing ? faSpinner : faFileInvoiceDollar}
                                onClick={() => handleProcess(company.companyCode)}
                                disabled={isProcessing}
                                isLoading={isProcessing}
                                main
                                fullWidth
                            />
                        )}
                        {isCompleted && (
                            <CompletedBadge align="center" gap="0.25rem">
                                <Text fontSize={11} color="success" bold>{t('batchReturn.completed', 'Completed')}</Text>
                            </CompletedBadge>
                        )}
                    </CompanyRow>
                );
            })}
        </Panel>
    );
};

export default BatchReturnProcessingPanel;

const Panel = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const SectionLabel = styled(Text)`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.textLight};
`;

const CompanyRow = styled(Flex)`
    padding: 0.5rem 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    &:last-child {
        border-bottom: none;
    }
`;

const ProgressBar = styled.div`
    height: 4px;
    background-color: ${({ theme }) => theme.colors.borderLight};
    border-radius: 2px;
    overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number }>`
    height: 100%;
    width: ${({ $percent }) => $percent}%;
    background-color: ${({ theme }) => theme.colors.success};
    transition: width 0.3s ease;
`;

const CompletedBadge = styled(Flex)`
    padding: 0.25rem 0;
`;
